import { useCallback, useEffect, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";

// Update runs: scrape in update mode, detect new/changed/removed items,
// import them and re-sync the search index (backend/update_runner.py).
// Shown on every provider's scrape control page.

const KINDS = [
  { key: "new", label: "Neu", className: "text-emerald-400" },
  { key: "restored", label: "Wieder da", className: "text-sky-400" },
  { key: "changed", label: "Geändert", className: "text-amber-400" },
  { key: "removed", label: "Entfernt", className: "text-red-400" },
];

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function RunStatus({ run }) {
  if (run.status === "running") {
    return (
      <span className="inline-flex items-center gap-1 text-neutral-300">
        <Loader2 className="w-3 h-3 animate-spin text-red-500" /> läuft
      </span>
    );
  }
  if (run.status === "complete") return <span className="text-emerald-400">vollständig</span>;
  return <span className="text-amber-400">unvollständig</span>;
}

export default function UpdatePanel({ provider }) {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [starting, setStarting] = useState(false);
  const [openRun, setOpenRun] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: r }] = await Promise.all([
        api.get("/updates/status"),
        api.get("/updates/runs", { params: { provider, limit: 10 } }),
      ]);
      setStatus(st);
      setRuns(r.runs || []);
    } catch (err) {
      console.warn("update status error", err);
    }
  }, [provider]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/updates/run", null, { params: { provider } });
      toast.success("Update-Lauf gestartet");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update-Lauf konnte nicht gestartet werden");
    } finally {
      setStarting(false);
    }
  };

  const busy = starting || status?.running;

  return (
    <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-4" data-testid={`update-panel-${provider}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-sm uppercase tracking-widest text-neutral-500">Updates</div>
          <p className="text-xs text-neutral-500 mt-1 max-w-xl">
            Scrapt im Update-Modus, erkennt neue, geänderte und entfernte Einträge, importiert sie und
            aktualisiert die Suche. Automatisch {status?.schedule || "nachts"}; entfernte Einträge
            werden archiviert, nicht gelöscht.
          </p>
        </div>
        <Button
          onClick={start}
          disabled={busy}
          className="bg-[#D32222] hover:bg-[#E13636] text-white"
          data-testid={`update-run-${provider}`}
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {status?.running ? "Update läuft…" : "Jetzt nach Updates suchen"}
        </Button>
      </div>

      {runs.length === 0 ? (
        <div className="text-sm text-neutral-500 py-4">Noch keine Update-Läufe.</div>
      ) : (
        <div className="border border-neutral-800/70 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                <th className="text-left font-normal px-3 py-2 w-6" />
                <th className="text-left font-normal px-3 py-2">Start</th>
                <th className="text-left font-normal px-3 py-2">Status</th>
                {KINDS.map((k) => (
                  <th key={k.key} className="text-right font-normal px-3 py-2">{k.label}</th>
                ))}
                <th className="text-right font-normal px-3 py-2">Unverändert</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <RunRow
                  key={run.id}
                  run={run}
                  open={openRun === run.id}
                  onToggle={() => setOpenRun(openRun === run.id ? null : run.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RunRow({ run, open, onToggle }) {
  const counts = run.counts || {};
  const hasChanges = KINDS.some((k) => counts[k.key] > 0);
  const syncError = run.index_sync?.error;
  return (
    <>
      <tr
        className={`border-b border-neutral-800/60 last:border-0 ${hasChanges ? "cursor-pointer hover:bg-neutral-800/30" : ""}`}
        onClick={hasChanges ? onToggle : undefined}
      >
        <td className="px-3 py-2 text-neutral-500">
          {hasChanges && (open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
        </td>
        <td className="px-3 py-2 text-neutral-300 whitespace-nowrap">{formatDate(run.started_at)}</td>
        <td className="px-3 py-2 whitespace-nowrap text-xs">
          <RunStatus run={run} />
          {run.removal_note && <div className="text-[11px] text-neutral-500 mt-0.5">{run.removal_note}</div>}
          {syncError && <div className="text-[11px] text-red-400 mt-0.5">Suche nicht aktualisiert</div>}
        </td>
        {KINDS.map((k) => (
          <td key={k.key} className={`px-3 py-2 text-right tabular-nums ${counts[k.key] ? k.className : "text-neutral-600"}`}>
            {run.status === "running" ? "…" : (counts[k.key] || 0).toLocaleString("de-DE")}
          </td>
        ))}
        <td className="px-3 py-2 text-right tabular-nums text-neutral-500">
          {run.status === "running" ? "…" : ((counts.unchanged || 0) + (counts.baseline || 0)).toLocaleString("de-DE")}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-neutral-800/60">
          <td colSpan={KINDS.length + 4} className="px-3 py-3 bg-neutral-900/40">
            <RunChanges run={run} />
          </td>
        </tr>
      )}
    </>
  );
}

function RunChanges({ run }) {
  const counts = run.counts || {};
  const available = KINDS.filter((k) => counts[k.key] > 0);
  const [kind, setKind] = useState(available[0]?.key);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(
    async (offset) => {
      setLoading(true);
      try {
        const { data } = await api.get(`/updates/runs/${run.id}/changes`, {
          params: { kind, limit: 100, offset },
        });
        setItems((prev) => (offset === 0 ? data.items : [...prev, ...data.items]));
        setTotal(data.total);
      } catch (err) {
        console.warn("changes error", err);
      } finally {
        setLoading(false);
      }
    },
    [run.id, kind],
  );

  useEffect(() => {
    if (kind) fetchPage(0);
  }, [kind, fetchPage]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {available.map((k) => (
          <button
            key={k.key}
            type="button"
            onClick={() => setKind(k.key)}
            className={`px-3 py-1 rounded-md text-xs border transition ${
              kind === k.key
                ? "border-red-500/60 bg-red-500/15 text-neutral-100"
                : "border-neutral-800 text-neutral-400 hover:text-neutral-100"
            }`}
          >
            {k.label} ({counts[k.key].toLocaleString("de-DE")})
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-neutral-400 py-3">
          <Loader2 className="w-4 h-4 animate-spin text-red-500" /> Lade Änderungen…
        </div>
      ) : (
        <ul className="divide-y divide-neutral-800/60 text-sm">
          {items.map((it) => (
            <li key={`${it.kind}-${it.item_id}`} className="py-1.5 flex flex-wrap items-baseline justify-between gap-x-4">
              <span className="text-neutral-200">{it.label || it.item_id}</span>
              {it.fields?.length > 0 && (
                <span className="text-[11px] text-neutral-500 font-mono">{it.fields.join(", ")}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {items.length < total && (
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => fetchPage(items.length)}
            className="border-neutral-800 bg-transparent text-neutral-300"
          >
            {loading && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
            Mehr laden
          </Button>
          <span className="text-xs text-neutral-500">
            {items.length.toLocaleString("de-DE")} von {total.toLocaleString("de-DE")}
          </span>
        </div>
      )}
      {!loading && total < (counts[kind] || 0) && (
        <div className="text-xs text-neutral-500">
          Detailliste auf {total.toLocaleString("de-DE")} Einträge begrenzt.
        </div>
      )}
    </div>
  );
}
